import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, MapPin, Users, Star, X, Heart, Vote, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import LocationFilter from './LocationFilter';

// Create a separate axios instance for postcode API without auth headers
const postcodeAPI = axios.create();

// Haversine formula to calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in miles
};

export default function BrowseRides({ user }) {
  const navigate = useNavigate();
  const [allRides, setAllRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [locationFilter, setLocationFilter] = useState({ active: false });
  const [rideCoordinates, setRideCoordinates] = useState({});
  const [locationRequired, setLocationRequired] = useState(true); // Require location to browse
  
  // Touch/swipe handling
  const cardRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const minSwipeDistance = 100;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    setDragOffset(currentTouch - touchStart);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd) {
      setDragOffset(0);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleSwipe('left');
    } else if (isRightSwipe) {
      handleSwipe('right');
    }
    
    setDragOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Mouse drag handling for desktop
  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !touchStart) return;
    const currentPos = e.clientX;
    setTouchEnd(currentPos);
    setDragOffset(currentPos - touchStart);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (touchStart && touchEnd) {
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;
      
      if (isLeftSwipe) {
        handleSwipe('left');
      } else if (isRightSwipe) {
        handleSwipe('right');
      }
    }
    
    setDragOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    fetchRides();
  }, []);

  useEffect(() => {
    applyLocationFilter();
  }, [locationFilter, allRides, rideCoordinates]);

  const [rideTypeFilter, setRideTypeFilter] = useState('individual');

  const fetchRides = async (type = rideTypeFilter) => {
    setLoading(true);
    try {
      console.log('[BrowseRides] Fetching rides, type:', type);
      console.log('[BrowseRides] API baseURL:', axios.defaults.baseURL);
      
      const response = await axios.get(`/rides/browse?ride_type=${type}`);
      console.log('[BrowseRides] SUCCESS - Rides received:', response.data?.length);
      setAllRides(response.data);
      setCurrentIndex(0);
      
      // Fetch coordinates for all ride postcodes
      await fetchRideCoordinates(response.data);
    } catch (error) {
      // VERBOSE PRODUCTION ERROR LOGGING - DO NOT REMOVE
      console.error('============================================');
      console.error('[BrowseRides] CRITICAL ERROR - fetchRides FAILED');
      console.error('[BrowseRides] Error name:', error.name);
      console.error('[BrowseRides] Error message:', error.message);
      console.error('[BrowseRides] Error code:', error.code);
      console.error('[BrowseRides] Response status:', error.response?.status);
      console.error('[BrowseRides] Response data:', JSON.stringify(error.response?.data));
      console.error('[BrowseRides] Full error object:', error);
      console.error('============================================');
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const handleRideTypeChange = (type) => {
    setRideTypeFilter(type);
    fetchRides(type);
  };

  const fetchRideCoordinates = async (rides) => {
    const coords = {};
    for (const ride of rides) {
      // First check if ride has lat/lng directly (new global location system)
      if (ride.latitude && ride.longitude) {
        coords[ride.id] = {
          lat: ride.latitude,
          lon: ride.longitude
        };
      } else if (ride.postcode) {
        // Fallback to UK postcode lookup for legacy rides
        try {
          const response = await postcodeAPI.get(`https://api.postcodes.io/postcodes/${ride.postcode}`);
          if (response.data.status === 200) {
            coords[ride.id] = {
              lat: response.data.result.latitude,
              lon: response.data.result.longitude
            };
          }
        } catch (error) {
          console.error(`Failed to fetch coordinates for ${ride.postcode}`);
        }
      }
    }
    setRideCoordinates(coords);
  };

  const applyLocationFilter = () => {
    if (!locationFilter.active || !locationFilter.coordinates) {
      setFilteredRides(allRides);
      setCurrentIndex(0);
      return;
    }

    const { coordinates, radiusMiles } = locationFilter;
    const filtered = allRides.filter(ride => {
      // Check if ride has coordinates (new system) or postcode (legacy)
      const rideCoords = rideCoordinates[ride.id];
      
      // If no coordinates available, exclude the ride from location-filtered results
      if (!rideCoords) return false;

      const distance = calculateDistance(
        coordinates.lat,
        coordinates.lon,
        rideCoords.lat,
        rideCoords.lon
      );

      return distance <= radiusMiles;
    });

    // Sort by distance (closest first)
    filtered.sort((a, b) => {
      const aCoordsValid = rideCoordinates[a.id];
      const bCoordsValid = rideCoordinates[b.id];
      
      if (!aCoordsValid && !bCoordsValid) return 0;
      if (!aCoordsValid) return 1;
      if (!bCoordsValid) return -1;
      
      const distA = calculateDistance(
        locationFilter.coordinates.lat,
        locationFilter.coordinates.lon,
        rideCoordinates[a.id].lat,
        rideCoordinates[a.id].lon
      );
      const distB = calculateDistance(
        locationFilter.coordinates.lat,
        locationFilter.coordinates.lon,
        rideCoordinates[b.id].lat,
        rideCoordinates[b.id].lon
      );
      
      return distA - distB;
    });

    setFilteredRides(filtered);
    setCurrentIndex(0);
  };

  const handleLocationFilterChange = (filter) => {
    setLocationFilter(filter);
  };

  const handleSwipe = async (action) => {
    if (currentIndex >= filteredRides.length) return;

    const currentRide = filteredRides[currentIndex];
    setSwipeDirection(action);

    try {
      const response = await axios.post(`/rides/${currentRide.id}/swipe?action=${action}`);
      
      if (action === 'right') {
        // Show appropriate message based on status
        const status = response.data.status;
        if (status === 'pending') {
          toast.success('Request sent! Waiting for host approval.');
        } else if (status === 'waitlisted') {
          toast.info('Added to waitlist. You\'ll be notified if a spot opens.');
        } else if (status === 'full') {
          toast.warning('This ride is full.');
        } else {
          toast.success('Joined the ride! Check your Joined Rides tab.');
        }
      }

      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setSwipeDirection(null);
      }, 300);
    } catch (error) {
      toast.error('Failed to process swipe');
      setSwipeDirection(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <p className="text-gray-400">Loading rides...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="browse-rides">
      {/* Location Filter */}
      <LocationFilter onFilterChange={handleLocationFilterChange} unitPreference={user?.unit_preference} />

      {/* Ride Type Tabs */}
      <Tabs value={rideTypeFilter} onValueChange={handleRideTypeChange} className="w-full mb-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="individual" className="data-[state=active]:bg-purple-600">
            🚴 Individual Rides
          </TabsTrigger>
          <TabsTrigger value="group" className="data-[state=active]:bg-purple-600">
            👥 Group Rides
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-gray-400">Loading rides...</p>
        </div>
      ) : !locationFilter.active ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-purple-500" />
          </div>
          <p className="text-foreground text-lg font-semibold mb-2">Set Your Location</p>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Use the search box above or tap the GPS button to find rides near you.
            We only show rides within your selected distance.
          </p>
        </div>
      ) : filteredRides.length === 0 || currentIndex >= filteredRides.length ? (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg mb-4">
            No rides found within {locationFilter.radiusMiles ? `${(locationFilter.radiusMiles * 1.60934).toFixed(0)}km` : 'your area'}
          </p>
          <p className="text-gray-500 text-sm">
            Try increasing your search radius or changing your location
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Group ride badge */}
          {filteredRides[currentIndex].ride_type === 'group' && (
            <div className="absolute -top-2 -right-2 z-20">
              <Badge className="bg-purple-600 text-white px-3 py-1">
                👥 Group Ride
              </Badge>
            </div>
          )}
          
          <Card
            ref={cardRef}
            className={`swipe-card rounded-2xl shadow-xl border-0 overflow-hidden cursor-grab active:cursor-grabbing select-none ${swipeDirection === 'left' ? 'swipe-left' : ''} ${swipeDirection === 'right' ? 'swipe-right' : ''}`}
            style={{
              transform: isDragging ? `translateX(${dragOffset}px) rotate(${dragOffset * 0.05}deg)` : 'none',
              transition: isDragging ? 'none' : 'transform 0.3s ease',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(139, 92, 246, 0.1)',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            data-testid="ride-card"
          >
            {/* Swipe indicator overlays */}
            {dragOffset > 50 && (
              <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center z-10 pointer-events-none rounded-2xl">
                <Heart className="w-24 h-24 text-green-500 opacity-70" />
              </div>
            )}
            {dragOffset < -50 && (
              <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center z-10 pointer-events-none rounded-2xl">
                <X className="w-24 h-24 text-red-500 opacity-70" />
              </div>
            )}
            
            <CardContent className="p-6">
              {/* Show distance if filter is active */}
              {locationFilter.active && rideCoordinates[filteredRides[currentIndex].id] && (
                <div className="mb-4 flex items-center gap-2 text-sm text-purple-400">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {(() => {
                      const distMiles = calculateDistance(
                        locationFilter.coordinates.lat,
                        locationFilter.coordinates.lon,
                        rideCoordinates[filteredRides[currentIndex].id].lat,
                        rideCoordinates[filteredRides[currentIndex].id].lon
                      );
                      const isMetric = user?.unit_preference === 'metric';
                      return isMetric 
                        ? `${(distMiles * 1.60934).toFixed(1)} km away`
                        : `${distMiles.toFixed(1)} miles away`;
                    })()}
                  </span>
                </div>
              )}
            {/* Creator Info - Clickable */}
            <div 
              className="flex items-center gap-3 mb-6 cursor-pointer hover:bg-accent p-2 rounded-lg transition-colors"
              onClick={() => navigate(`/profile/${filteredRides[currentIndex].creator?.id}`)}
            >
              {filteredRides[currentIndex].creator?.profile_picture ? (
                <img
                  src={filteredRides[currentIndex].creator.profile_picture}
                  alt={filteredRides[currentIndex].creator.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-purple-600"
                />
              ) : (
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {filteredRides[currentIndex].creator?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-foreground font-semibold text-lg hover:text-purple-500 transition-colors" data-testid="creator-name">
                  {filteredRides[currentIndex].creator?.name}
                </h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <span className="text-sm text-muted-foreground" data-testid="creator-rating">
                      {filteredRides[currentIndex].creator?.avg_rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">
                    {filteredRides[currentIndex].creator?.experience_level}
                  </span>
                </div>
                <p className="text-xs text-purple-500 mt-1">Tap to view profile</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <Badge variant="secondary" data-testid="ride-distance">{filteredRides[currentIndex].distance}km</Badge>
                {filteredRides[currentIndex].category && (
                  <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-500">
                    {filteredRides[currentIndex].category === 'road' && '🛣️ Road'}
                    {filteredRides[currentIndex].category === 'mountain' && '⛰️ Mountain'}
                    {filteredRides[currentIndex].category === 'gravel' && '🏞️ Gravel'}
                    {filteredRides[currentIndex].category === 'commute' && '🚴 Commute'}
                  </Badge>
                )}
              </div>
            </div>

            {/* Ride Details */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-foreground font-medium">Date & Time</p>
                  <p className="text-muted-foreground text-sm" data-testid="ride-datetime">{filteredRides[currentIndex].date} at {filteredRides[currentIndex].time}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-purple-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-foreground font-medium">Start Location</p>
                  {/* Show formatted address if available, otherwise fallback to postcode */}
                  {filteredRides[currentIndex].formatted_address ? (
                    <p className="text-muted-foreground text-sm" data-testid="ride-location">
                      {filteredRides[currentIndex].formatted_address}
                    </p>
                  ) : filteredRides[currentIndex].postcode ? (
                    <p className="text-muted-foreground text-sm font-mono" data-testid="ride-postcode">
                      {filteredRides[currentIndex].postcode}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-sm">Location not specified</p>
                  )}
                  {filteredRides[currentIndex].location && (
                    <p className="text-muted-foreground text-xs mt-1">{filteredRides[currentIndex].location}</p>
                  )}
                  {/* Open in Google Maps button */}
                  {(filteredRides[currentIndex].latitude && filteredRides[currentIndex].longitude) ? (
                    <a
                      href={`https://www.google.com/maps?q=${filteredRides[currentIndex].latitude},${filteredRides[currentIndex].longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-purple-500 hover:text-purple-400 mt-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open in Google Maps
                    </a>
                  ) : filteredRides[currentIndex].postcode && (
                    <a
                      href={`https://www.google.com/maps/search/${encodeURIComponent(filteredRides[currentIndex].postcode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-purple-500 hover:text-purple-400 mt-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open in Google Maps
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-foreground font-medium">Riders</p>
                  <p className="text-muted-foreground text-sm" data-testid="ride-participants">
                    {filteredRides[currentIndex].participants_count + 1}{filteredRides[currentIndex].max_riders ? ` / ${filteredRides[currentIndex].max_riders + 1}` : ' (unlimited)'}
                  </p>
                </div>
              </div>

              {/* Route Links Section - Always visible */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-purple-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <div className="flex-1">
                  <p className="text-foreground font-medium">Route Map</p>
                  {filteredRides[currentIndex].ride_type === 'group' && filteredRides[currentIndex].route_options?.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {filteredRides[currentIndex].route_options.map((route, idx) => (
                        <a
                          key={idx}
                          href={route.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-foreground text-sm">{route.name || `Route ${idx + 1}`}</span>
                          <span className="text-purple-500 text-xs flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Preview
                          </span>
                        </a>
                      ))}
                      <p className="text-xs text-muted-foreground mt-1">Vote for your preferred route after joining</p>
                    </div>
                  ) : filteredRides[currentIndex].route_link ? (
                    <a
                      href={filteredRides[currentIndex].route_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors mt-2"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="ride-route-link"
                    >
                      <ExternalLink className="w-4 h-4 text-purple-500" />
                      <span className="text-purple-500 text-sm">View Route on GPS</span>
                    </a>
                  ) : (
                    <p className="text-muted-foreground text-sm">No route uploaded</p>
                  )}
                </div>
              </div>

              {/* Waitlist info for full group rides */}
              {filteredRides[currentIndex].ride_type === 'group' && 
               filteredRides[currentIndex].max_riders && 
               filteredRides[currentIndex].participants_count >= filteredRides[currentIndex].max_riders && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-yellow-500 font-semibold text-xs uppercase tracking-wider mb-1">
                    🎫 Ride Full - Waitlist Available
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {filteredRides[currentIndex].waitlist_count || 0} riders on waitlist
                  </p>
                </div>
              )}

              {filteredRides[currentIndex].route_description && (
                <div>
                  <p className="text-foreground font-medium mb-2">Route</p>
                  <p className="text-muted-foreground text-sm" data-testid="ride-route">{filteredRides[currentIndex].route_description}</p>
                </div>
              )}

              {filteredRides[currentIndex].creator?.bio && (
                <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <p className="text-purple-500 font-semibold text-xs uppercase tracking-wider mb-2">About {filteredRides[currentIndex].creator.name}</p>
                  <p className="text-foreground text-sm italic">&ldquo;{filteredRides[currentIndex].creator.bio}&rdquo;</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Swipe Buttons */}
        <div className="flex justify-center gap-6 mt-8">
          <Button
            size="lg"
            variant="outline"
            className="w-16 h-16 rounded-full border-red-500 hover:bg-red-500/20"
            onClick={() => handleSwipe('left')}
            data-testid="swipe-left-button"
          >
            <X className="w-8 h-8 text-red-500" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="w-16 h-16 rounded-full border-yellow-500 hover:bg-yellow-500/20"
            onClick={() => setCurrentIndex(prev => prev + 1)}
            data-testid="swipe-maybe-button"
          >
            <span className="text-yellow-500 font-bold text-lg">?</span>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="w-16 h-16 rounded-full border-green-500 hover:bg-green-500/20"
            onClick={() => handleSwipe('right')}
            data-testid="swipe-right-button"
          >
            <Heart className="w-8 h-8 text-green-500" />
          </Button>
        </div>

        <p className="text-center text-gray-500 text-sm mt-4">
          {currentIndex + 1} / {filteredRides.length}
        </p>
        <p className="text-center text-gray-600 text-xs mt-2">
          👆 Swipe the card or use the buttons
        </p>
      </div>
      )}
    </div>
  );
}
